<template>
  <div>
    <FieldWrapper v-if="editor" :field="field">
      <div class="form-control px-1 h-auto" :class="{ disabled: isDisabled }">
        <!------------ START: Editor toolbar with locale switch ------------>
        <div class="d-flex align-items-center px-3">
          <div class="flex-grow-1">
            <EditorToolbar :editor="editor" />
          </div>
          <div class="locale-switch ml-2 d-flex">
            <button
              v-for="locale in locales"
              :key="locale.key"
              type="button"
              class="locale-switch__btn"
              :class="{
                'locale-switch__btn--active': activeLocale === locale.key
              }"
              @click="switchLocale(locale.key)"
            >
              <img
                :src="locale.flag"
                :alt="locale.label"
                class="locale-switch__flag"
              />
            </button>
          </div>
        </div>
        <!------------ END: Editor toolbar ------------>
        <div v-if="!isDisabled" class="separator separator-solid my-2" />
        <!------------ START: Editor content ------------>
        <div class="px-3 mb-n3">
          <EditorContent :editor="editor" />
        </div>
        <!------------ END: Editor content ------------>
        <!------------ START: Editor validation errors ------------>
        <div
          v-if="validationErrors.length"
          class="separator separator-solid mb-2 mt-5"
        />
        <ul v-if="validationErrors.length" class="mx-3 mb-0">
          <li
            v-for="(error, i) in validationErrors"
            :key="i"
            class="text-nowrap text-left text-danger"
          >
            {{ error }}
          </li>
        </ul>
        <!------------ END: Editor validation errors ------------>
      </div>
    </FieldWrapper>
  </div>
</template>

<script>
import FieldWrapper from "@/components/Tools/FormHelper/Components/FieldWrapper";
import {
  base,
  eagerValidation
} from "@/components/Tools/FormHelper/Helper/mixins";
import EditorToolbar from "@/components/Tools/FormHelper/Components/Editor/EditorToolbar";
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import {
  markdownToHtml,
  htmlToMarkdown
} from "@/components/Tools/FormHelper/Helper/markdownService";
import {
  parseLocalizedString,
  getLocalizedStringLocales
} from "@/composables/useLocalizedDescription";
import i18n from "@/core/plugins/vue-i18n";

export default {
  components: { EditorContent, FieldWrapper, EditorToolbar },
  mixins: [base, eagerValidation],
  data() {
    return {
      editor: null,
      activeLocale: i18n.locale,
      locales: getLocalizedStringLocales(),
      localeMarkdown: {},
      suppressSync: false
    };
  },
  computed: {
    // Override mixin's `value` so vuelidate validators run against the longest
    // locale text instead of the raw JSON envelope.
    value: {
      get() {
        let longest = "";
        for (const key of Object.keys(this.localeMarkdown)) {
          const text = this.localeMarkdown[key] ?? "";
          if (text.length > longest.length) longest = text;
        }
        return longest;
      },
      set(val) {
        this.$emit("input", val);
      }
    }
  },
  watch: {
    isDisabled() {
      this.setEditable();
    },
    defaultValue: {
      immediate: true,
      handler(newVal) {
        if (!this.suppressSync && newVal) {
          this.syncFromModel(newVal);
          this.loadLocaleIntoEditor(this.activeLocale);
        }
      }
    }
  },
  mounted() {
    this.initEditor();
    this.setEditable();
  },
  beforeDestroy() {
    this.editor?.destroy();
  },
  methods: {
    syncFromModel(raw) {
      const parsed = parseLocalizedString(raw);
      for (const locale of this.locales) {
        this.$set(this.localeMarkdown, locale.key, parsed[locale.key] ?? "");
      }
    },
    initEditor() {
      const currentMarkdown = this.localeMarkdown[this.activeLocale] ?? "";
      this.editor = new Editor({
        content: markdownToHtml(currentMarkdown),
        extensions: [StarterKit],
        onUpdate: () => {
          if (this.suppressSync) return;
          const markdown = htmlToMarkdown(this.editor.getHTML());
          this.$set(this.localeMarkdown, this.activeLocale, markdown);
          this.writeBack();
        }
      });
    },
    switchLocale(locale) {
      // Save current locale content
      const currentMarkdown = htmlToMarkdown(this.editor.getHTML());
      this.$set(this.localeMarkdown, this.activeLocale, currentMarkdown);

      // Switch to new locale
      this.activeLocale = locale;
      this.loadLocaleIntoEditor(locale);
    },
    loadLocaleIntoEditor(locale) {
      if (!this.editor) return;
      this.suppressSync = true;
      const markdown = this.localeMarkdown[locale] ?? "";
      this.editor.commands.setContent(markdownToHtml(markdown));
      this.$nextTick().then(() => {
        this.suppressSync = false;
      });
    },
    writeBack() {
      const values = {};
      for (const locale of this.locales) {
        if (this.localeMarkdown[locale.key]) {
          values[locale.key] = this.localeMarkdown[locale.key];
        }
      }
      this.suppressSync = true;
      this.$emit("input", JSON.stringify(values));
      this.$nextTick().then(() => {
        this.suppressSync = false;
      });
    },
    setEditable() {
      this.editor.setEditable(!this.isDisabled);
    },
    validate() {
      this.$v.$touch();
      return !this.$v.$anyError;
    }
  }
};
</script>

<style lang="scss" scoped>
.locale-switch {
  gap: 2px;
  flex-shrink: 0;

  &__btn {
    display: flex;
    align-items: center;
    padding: 4px 6px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: none;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #f3f3f3;
    }

    &--active {
      background: #f3f3f3;
      border-color: #d0d0d0;
    }
  }

  &__flag {
    width: 20px;
    height: 14px;
    object-fit: cover;
    border-radius: 3px;
  }
}
</style>

<style lang="scss">
.ProseMirror {
  outline: none;
  min-height: 150px;
}
</style>
